import {
  AcraAssessmentResponse,
  AcraBulkCandidatesResponse,
  AcraCampaignNameCheckResponse,
  AcraCampaignResponse,
  AcraCampaignsResponse,
  AcraCandidatePhotoResponse,
  AcraCandidateResponse,
  AcraCandidatesResponse,
  AcraInviteCandidatesResponse,
  AcraSessionStatsResponse,
  APIResponse,
  TAcraCampaignFilters,
  TAcraCandidateInput,
} from '@answerr/types';
import {
  AcraAssessmentInput,
  CreateAcraCampaignInput,
  UpdateAcraCampaignInput,
  UpdateAcraCandidateInput,
} from '../components/main/acra/types';
import http from '../utils/http-common';

const createCampaign = (data: CreateAcraCampaignInput) => {
  return http.post<AcraCampaignResponse>('/acra/campaigns/create', data);
};

// Filtering happens server-side, so the list is queried rather than trimmed in
// the browser. Only the filters the admin actually set travel — the API reads
// an absent key as "no filter", and an empty string is not a filter.
const getAllCampaigns = (filters: TAcraCampaignFilters = {}) => {
  const payload: TAcraCampaignFilters = {};
  const search = filters.search?.trim();
  if (search) payload.search = search;
  if (filters.status) payload.status = filters.status;
  if (filters.deadlineFrom) payload.deadlineFrom = filters.deadlineFrom;
  if (filters.deadlineTo) payload.deadlineTo = filters.deadlineTo;
  return http.post<AcraCampaignsResponse>('/acra/campaigns/list', payload);
};

const getCampaignById = (campaignId: string) => {
  return http.post<AcraCampaignResponse>('/acra/campaigns/get', { campaignId });
};

const updateCampaign = (data: UpdateAcraCampaignInput) => {
  return http.post<AcraCampaignResponse>('/acra/campaigns/update', data);
};

// Campaign names are unique per organisation. `campaignId` is sent in edit mode
// so the campaign being renamed is not counted as a clash with itself.
const checkCampaignName = (name: string, campaignId?: string) => {
  return http.post<AcraCampaignNameCheckResponse>(
    '/acra/campaigns/check-name',
    campaignId ? { name, campaignId } : { name }
  );
};

const deleteCampaign = (campaignId: string) => {
  return http.post<APIResponse>('/acra/campaigns/delete', { campaignId });
};

const addCandidate = (campaignId: string, data: TAcraCandidateInput) => {
  return http.post<AcraCandidateResponse>('/acra/candidates/add', {
    campaignId,
    ...data,
  });
};

const addCandidatesBulk = (
  campaignId: string,
  candidates: TAcraCandidateInput[]
) => {
  return http.post<AcraBulkCandidatesResponse>('/acra/candidates/bulk-add', {
    campaignId,
    candidates,
  });
};

const getCandidatesByCampaign = (campaignId: string) => {
  return http.post<AcraCandidatesResponse>('/acra/candidates/list', {
    campaignId,
  });
};

const updateCandidate = (data: UpdateAcraCandidateInput) => {
  return http.post<AcraCandidateResponse>('/acra/candidates/update', data);
};

const deleteCandidate = (candidateId: string) => {
  return http.post<APIResponse>('/acra/candidates/delete', { candidateId });
};

const sendInviteCandidates = (campaignId: string) => {
  return http.post<AcraInviteCandidatesResponse>(
    '/acra/candidates/send-invite',
    { campaignId }
  );
};

const sendInviteCandidate = (candidateId: string) => {
  return http.post<AcraCandidateResponse>(
    '/acra/candidates/send-invite-single',
    { candidateId }
  );
};

const getCandidateSessionStats = (candidateId: string) => {
  return http.post<AcraSessionStatsResponse>(
    '/acra/candidates/session-stats',
    { candidateId }
  );
};

// The identity photo as a data URI. The candidate list already carries a signed
// URL for display; this exists for the credential, which is turned into a PDF by
// capturing the DOM and so needs pixels the canvas may read back.
const getCandidatePhoto = (candidateId: string) => {
  return http.post<AcraCandidatePhotoResponse>('/acra/candidates/photo', {
    candidateId,
  });
};

const createAssessment = (data: AcraAssessmentInput) => {
  return http.post<AcraAssessmentResponse>('/acra/assessments/create', data);
};

const getAssessment = (campaignId: string) => {
  return http.post<AcraAssessmentResponse>('/acra/assessments/get', {
    campaignId,
  });
};

const regenerateAssessment = (data: AcraAssessmentInput) => {
  return http.post<AcraAssessmentResponse>('/acra/assessments/regenerate', data);
};

const AcraService = {
  createCampaign,
  getAllCampaigns,
  getCampaignById,
  updateCampaign,
  checkCampaignName,
  deleteCampaign,
  addCandidate,
  addCandidatesBulk,
  getCandidatesByCampaign,
  updateCandidate,
  deleteCandidate,
  sendInviteCandidates,
  sendInviteCandidate,
  getCandidateSessionStats,
  getCandidatePhoto,
  createAssessment,
  getAssessment,
  regenerateAssessment,
};

export default AcraService;
